import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import toast from '../utils/toast';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';

const CustomPageSubmissionsTable = () => {
  const { pageId } = useParams();
  const { checkPermission } = useAuth();
  const navigate = useNavigate();

  const [customPage, setCustomPage] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  // Column visibility state
  const [columnVisibility, setColumnVisibility] = useState({
    status: true,
    submittedBy: true,
    submittedDate: true
  });

  const canView = checkPermission('View Custom Page Submissions');
  const canCreate = checkPermission('Create Custom Page Submission');
  const canUpdate = checkPermission('Update Custom Page Submission');
  const canDelete = checkPermission('Delete Custom Page Submission');

  useEffect(() => {
    fetchCustomPage();
    fetchSubmissions();
  }, [pageId, pagination.page, searchTerm, selectedStatus]);

  const fetchCustomPage = async () => {
    try {
      const response = await api.get(`/custom-pages/${pageId}`);
      if (response.data.success) {
        setCustomPage(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching custom page:', error);
      toast.error('Failed to fetch page details');
    }
  };

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      if (selectedStatus) {
        params.append('status', selectedStatus);
      }

      params.append('page', pagination.page.toString());
      params.append('limit', pagination.limit.toString());

      const response = await api.get(`/custom-pages/${pageId}/submissions?${params.toString()}`);

      if (response.data.success) {
        setSubmissions(response.data.data.docs);
        setPagination(prev => ({
          ...prev,
          total: response.data.data.totalDocs,
          pages: response.data.data.totalPages
        }));
      }
    } catch (error) {
      console.error('Error fetching submissions:', error);
      toast.error('Failed to fetch submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (submission) => {
    try {
      const response = await api.get(`/custom-pages/submissions/${submission._id}`);
      if (response.data.success) {
        setSelectedSubmission(response.data.data);
        setShowDetails(true);
      }
    } catch (error) {
      console.error('Error fetching submission details:', error);
      toast.error('Failed to fetch submission details');
    }
  };

  const handleEdit = (submission) => {
    setSelectedSubmission(submission);
    setEditFormData(submission.data || {});
    setShowEdit(true);
  };

  const handleDelete = (submission) => {
    setSelectedSubmission(submission);
    setShowDelete(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/custom-pages/submissions/${selectedSubmission._id}`);
      toast.success('Submission deleted successfully');
      setShowDelete(false);
      setSelectedSubmission(null);
      fetchSubmissions();
    } catch (error) {
      console.error('Error deleting submission:', error);
      toast.error('Failed to delete submission');
    }
  };

  const handleStatusChange = async (submissionId, newStatus) => {
    try {
      const response = await api.put(`/custom-pages/submissions/${submissionId}`, {
        status: newStatus
      });

      if (response.data.success) {
        toast.success('Status updated successfully');
        fetchSubmissions();
        if (selectedSubmission && selectedSubmission._id === submissionId) {
          setSelectedSubmission(response.data.data);
        }
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleEditSave = async (updatedData) => {
    try {
      console.log('handleEditSave called with updatedData:', updatedData);

      // updatedData now contains { name, data } structure
      const response = await api.put(`/custom-pages/submissions/${selectedSubmission._id}`, updatedData);

      console.log('Update response:', response.data);

      if (response.data.success) {
        toast.success('Submission updated successfully');
        setShowEdit(false);
        setSelectedSubmission(null);
        setEditFormData({});
        fetchSubmissions();
      }
    } catch (error) {
      console.error('Error updating submission:', error);
      toast.error(error.response?.data?.message || 'Failed to update submission');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'submitted':
        return 'bg-blue-100 text-blue-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'completed':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderFieldValue = (field, value) => {
    if (!value) return '-';

    switch (field.type) {
      case 'upload':
        if (Array.isArray(value) && value.length > 0) {
          return (
            <div className="space-y-1">
              {value.map((file, index) => (
                <div key={index} className="text-sm text-blue-600 hover:text-blue-800">
                  <a href={file.url} target="_blank" rel="noopener noreferrer">
                    {file.name}
                  </a>
                </div>
              ))}
            </div>
          );
        }
        return 'No files uploaded';

      case 'masterdata':
        if (value && typeof value === 'object') {
          return value.clientName || 'N/A';
        }
        return value || '-';

      case 'user':
        if (value && typeof value === 'object') {
          return `${value.firstName} ${value.lastName}`;
        }
        return value || '-';

      case 'date':
        return formatDate(value);

      case 'multitext':
        return value.length > 100 ? `${value.substring(0, 100)}...` : value;

      default:
        return value;
    }
  };

  // Get fields that should be shown in table
  const getTableFields = () => {
    const fields = [];

    // Add name column first
    fields.push({
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (submission) => (
        <div className="font-medium text-gray-900">
          {submission.name || 'Unnamed Submission'}
        </div>
      )
    });

    // Add deadline column if custom page has deadline field
    if (customPage?.taskSettings?.deadlineField) {
      fields.push({
        key: 'deadline',
        label: 'Deadline',
        sortable: true,
        render: (submission) => submission.deadline ? formatDate(submission.deadline) : '-'
      });
    }

    // Add custom fields that are marked to show in table
    if (customPage?.fields) {
      customPage.fields.forEach(field => {
        if (field.showInTable) {
          fields.push({
            key: field.name,
            label: field.label,
            sortable: false,
            render: (submission) => renderFieldValue(field, submission.data[field.name])
          });
        }
      });
    }

    // Add status column at the end (if visible)
    if (columnVisibility.status) {
      fields.push({
        key: 'status',
        label: 'Status',
        sortable: true,
        render: (submission) => (
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(submission.status)}`}>
            {submission.status}
          </span>
        )
      });
    }

    // Add submitted by column at the end (if visible)
    if (columnVisibility.submittedBy) {
      fields.push({
        key: 'submittedBy',
        label: 'Submitted By',
        sortable: true,
        render: (submission) => (
          <div>
            <div className="font-medium text-gray-900">
              {submission.submittedBy?.firstName} {submission.submittedBy?.lastName}
            </div>
            <div className="text-sm text-gray-500">{submission.submittedBy?.email}</div>
          </div>
        )
      });
    }

    // Add submitted date column at the end (if visible)
    if (columnVisibility.submittedDate) {
      fields.push({
        key: 'createdAt',
        label: 'Submitted Date',
        sortable: true,
        render: (submission) => formatDate(submission.createdAt)
      });
    }

    // Add actions column
    fields.push({
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (submission) => (
        <div className="flex space-x-2">
          <button
            onClick={() => handleViewDetails(submission)}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            View
          </button>
          {canUpdate && (
            <button
              onClick={() => handleEdit(submission)}
              className="text-green-600 hover:text-green-800 text-sm font-medium"
            >
              Edit
            </button>
          )}
          {canDelete && (
            <button
              onClick={() => handleDelete(submission)}
              className="text-red-600 hover:text-red-800 text-sm font-medium"
            >
              Delete
            </button>
          )}
        </div>
      )
    });

    return fields;
  };

  if (loading && submissions.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!customPage) {
    return (
      <div className="text-center py-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Page Not Found</h2>
        <p className="text-gray-600">The requested page could not be found.</p>
      </div>
    );
  }

  if (!canView) {
    return (
      <div className="text-center py-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
        <p className="text-gray-600">You don't have permission to view submissions.</p>
      </div>
    );
  }

  const tableFields = getTableFields();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{customPage.menuName} Submissions</h1>
          <p className="text-gray-600">{customPage.description}</p>
        </div>
        {canCreate && (
          <button
            onClick={() => navigate(`/custom-page/${pageId}`)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            New Submission
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search submissions..."
                className="pl-10 w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="submitted">Submitted</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Column Visibility</label>
            <div className="flex flex-wrap gap-2">
              <label className="flex items-center text-sm">
                <input
                  type="checkbox"
                  checked={columnVisibility.status}
                  onChange={(e) => setColumnVisibility(prev => ({ ...prev, status: e.target.checked }))}
                  className="mr-1"
                />
                Status
              </label>
              <label className="flex items-center text-sm">
                <input
                  type="checkbox"
                  checked={columnVisibility.submittedBy}
                  onChange={(e) => setColumnVisibility(prev => ({ ...prev, submittedBy: e.target.checked }))}
                  className="mr-1"
                />
                Submitted By
              </label>
              <label className="flex items-center text-sm">
                <input
                  type="checkbox"
                  checked={columnVisibility.submittedDate}
                  onChange={(e) => setColumnVisibility(prev => ({ ...prev, submittedDate: e.target.checked }))}
                  className="mr-1"
                />
                Submitted Date
              </label>
            </div>
          </div>
          <div className="flex items-end">
            <button
              onClick={fetchSubmissions}
              className="w-full bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <FunnelIcon className="h-4 w-4 inline mr-2" />
              Filter
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {/* Dynamic columns based on showInTable fields */}
                {tableFields.map((field) => (
                  <th key={field.key} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {field.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {submissions.map((submission) => (
                <tr key={submission._id} className="hover:bg-gray-50">
                  {/* Dynamic field columns */}
                  {tableFields.map((field) => (
                    <td key={field.key} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {field.render(submission)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {submissions.length === 0 && !loading && (
          <div className="text-center py-8">
            <p className="text-gray-500">No submissions found</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-700">
            Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
            {pagination.total} results
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              disabled={pagination.page === 1}
              className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>
            <span className="px-3 py-1 text-sm text-gray-700">
              Page {pagination.page} of {pagination.pages}
            </span>
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              disabled={pagination.page === pagination.pages}
              className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Submission Details Modal */}
      {showDetails && selectedSubmission && (
        <SubmissionDetailsModal
          submission={selectedSubmission}
          customPage={customPage}
          onClose={() => {
            setShowDetails(false);
            setSelectedSubmission(null);
          }}
          onStatusChange={handleStatusChange}
          canUpdate={canUpdate}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDelete && selectedSubmission && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Confirm Delete</h3>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete this submission? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDelete(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Submission Modal */}
      {showEdit && selectedSubmission && customPage && (
        <EditSubmissionModal
          submission={selectedSubmission}
          customPage={customPage}
          onClose={() => {
            setShowEdit(false);
            setSelectedSubmission(null);
            setEditFormData({});
          }}
          onSave={handleEditSave}
        />
      )}
    </div>
  );
};

// Submission Details Modal Component
const SubmissionDetailsModal = ({ submission, customPage, onClose, onStatusChange, canUpdate }) => {
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderFieldValue = (field, value) => {
    if (!value) return '-';

    switch (field.type) {
      case 'upload':
        if (Array.isArray(value) && value.length > 0) {
          return (
            <div className="space-y-1">
              {value.map((file, index) => (
                <div key={index} className="text-sm text-blue-600 hover:text-blue-800">
                  <a href={file.url} target="_blank" rel="noopener noreferrer">
                    {file.name}
                  </a>
                </div>
              ))}
            </div>
          );
        }
        return 'No files uploaded';

      case 'masterdata':
        if (value && typeof value === 'object') {
          return value.clientName || 'N/A';
        }
        return value || '-';

      case 'user':
        if (value && typeof value === 'object') {
          return `${value.firstName} ${value.lastName}`;
        }
        return value || '-';

      case 'date':
        return formatDate(value);

      case 'multitext':
        return value;

      default:
        return value;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'submitted':
        return 'bg-blue-100 text-blue-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'completed':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-semibold">Submission Details</h2>
            <p className="text-sm text-gray-600 mt-1">{submission.name || 'Unnamed Submission'}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Submission Name</label>
              <p className="text-sm text-gray-900 font-medium">
                {submission.name || 'Unnamed Submission'}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(submission.status)}`}>
                {submission.status}
              </span>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Submitted By</label>
              <p className="text-sm text-gray-900">
                {submission.submittedBy?.firstName} {submission.submittedBy?.lastName}
              </p>
              <p className="text-sm text-gray-500">{submission.submittedBy?.email}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Submitted Date</label>
              <p className="text-sm text-gray-900">{formatDate(submission.createdAt)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Deadline</label>
              <p className="text-sm text-gray-900">
                {submission.deadline ? formatDate(submission.deadline) : '-'}
              </p>
            </div>
          </div>

          {/* Form Data */}
          <div>
            <h3 className="text-lg font-medium mb-4">Form Data</h3>
            <div className="space-y-4">
              {customPage.fields.map((field, index) => (
                <div key={index} className="border-b border-gray-200 pb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {field.label}
                  </label>
                  <div className="text-sm text-gray-900">
                    {renderFieldValue(field, submission.data[field.name])}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Checklist */}
          {submission.documentVerifications && submission.documentVerifications.length > 0 && (
            <div>
              <h3 className="text-lg font-medium mb-4">Checklist</h3>
              <div className="space-y-4">
                {submission.documentVerifications.map((docVer, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="font-medium text-gray-900">{docVer.documentName}</h4>
                        {docVer.documentId?.description && (
                          <p className="text-sm text-gray-600 mt-1">{docVer.documentId.description}</p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Required
                        </span>
                        {docVer.fileUrl && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Available
                          </span>
                        )}
                      </div>
                    </div>

                    {docVer.fileUrl ? (
                      <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-900">{docVer.fileName}</span>
                        </div>
                        <a
                          href={docVer.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          Download
                        </a>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No file available</p>
                    )}

                    {docVer.documentId?.category && (
                      <p className="text-xs text-gray-500 mt-2">
                        Category: {docVer.documentId.category}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {(!submission.documentVerifications || submission.documentVerifications.length === 0) && (
            <div>
              <h3 className="text-lg font-medium mb-4">Checklist</h3>
              <div className="text-center py-4 border border-gray-200 rounded-lg">
                <p className="text-gray-500">No Checklist required for this submission.</p>
              </div>
            </div>
          )}

          {/* Status Update */}
          {canUpdate && (
            <div>
              <h3 className="text-lg font-medium mb-4">Update Status</h3>
              <select
                value={submission.status}
                onChange={(e) => onStatusChange(submission._id, e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="draft">Draft</option>
                <option value="submitted">Submitted</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Edit Submission Modal Component
const EditSubmissionModal = ({ submission, customPage, onClose, onSave }) => {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [submissionName, setSubmissionName] = useState('');
  const [users, setUsers] = useState([]);
  const [masterData, setMasterData] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [masterDataSearchTerm, setMasterDataSearchTerm] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showMasterDataDropdown, setShowMasterDataDropdown] = useState(false);
  const [documentVerifications, setDocumentVerifications] = useState([]);
  const [documentUploads, setDocumentUploads] = useState({});

  useEffect(() => {
    // Initialize form data with current submission data
    if (submission && submission.data) {
      setFormData(submission.data);
      setSubmissionName(submission.name || '');

      // Initialize search terms for user and masterdata fields
      const userField = customPage?.fields.find(f => f.type === 'user');
      const masterDataField = customPage?.fields.find(f => f.type === 'masterdata');

      if (userField && submission.data[userField.name]) {
        const user = submission.data[userField.name];
        setUserSearchTerm(`${user.firstName} ${user.lastName}`);
      }

      if (masterDataField && submission.data[masterDataField.name]) {
        const masterDataItem = submission.data[masterDataField.name];
        setMasterDataSearchTerm(masterDataItem.clientName || '');
      }
    }

    // Fetch users and master data
    fetchUsers();
    fetchMasterData();
    fetchDocumentVerifications();
  }, [submission, customPage]);

  // Click outside handler to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.user-dropdown')) {
        setShowUserDropdown(false);
      }
      if (!event.target.closest('.masterdata-dropdown')) {
        setShowMasterDataDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      if (response.data.success) {
        setUsers(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchMasterData = async () => {
    try {
      const response = await api.get('/master-data');
      setMasterData(response.data.data);
    } catch (error) {
      console.error('Error fetching master data:', error);
    }
  };

  const fetchDocumentVerifications = async () => {
    try {
      const response = await api.get('/custom-documents');
      if (response.data.success) {
        const docs = response.data.data.docs || response.data.data || [];
        setDocumentVerifications(docs);

        // Initialize document uploads from existing Checklist
        const initialUploads = {};
        if (submission?.documentVerifications) {
          submission.documentVerifications.forEach(docVer => {
            // If there are files in the Checklist, use the first one
            // Otherwise, create a file object from the Checklist data
            if (docVer.files && docVer.files.length > 0) {
              initialUploads[docVer.documentId] = docVer.files[0];
            } else if (docVer.fileUrl) {
              // Create a file object from the Checklist data
              initialUploads[docVer.documentId] = {
                fileName: docVer.fileName || docVer.documentName,
                fileUrl: docVer.fileUrl,
                originalName: docVer.fileName || docVer.documentName,
                mimeType: 'application/octet-stream', // Default mime type
                size: 0 // Unknown size
              };
            }
          });
        }
        setDocumentUploads(initialUploads);
      }
    } catch (error) {
      console.error('Error fetching Checklist:', error);
    }
  };

  const handleInputChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));

    // Clear error for this field
    if (errors[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: null
      }));
    }
  };

  const handleDocumentUpload = (documentId, files) => {
    setDocumentUploads(prev => ({
      ...prev,
      [documentId]: files
    }));
  };

  const uploadFileToServer = async (file) => {
    try {
      console.log('uploadFileToServer called with file:', file);

      const formData = new FormData();
      formData.append('file', file);

      console.log('Sending upload request to /upload');
      const response = await api.post('/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log('Upload response:', response.data);

      if (response.data.success) {
        const uploadedFile = {
          fileName: file.name,
          fileUrl: response.data.url,
          originalName: file.name,
          mimeType: file.type,
          size: file.size
        };
        console.log('Returning uploaded file object:', uploadedFile);
        return uploadedFile;
      } else {
        console.error('Upload failed - response not successful:', response.data);
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
      return null;
    }
  };

  const handleFileUpload = async (documentId, files) => {
    try {
      console.log('handleFileUpload called with:', { documentId, files });
      
      // If no files provided, clear the upload
      if (!files || files.length === 0) {
        console.log('No files provided, clearing document uploads for', documentId);
        handleDocumentUpload(documentId, null);
        return;
      }
      
      // Take the first file only (since we're using single file structure)
      const file = files[0];
      console.log('Processing file:', file);
      
      // If the file is already a File object (new upload), upload it
      if (file instanceof File) {
        console.log('Uploading new file to server:', file.name);
        const uploadedFile = await uploadFileToServer(file);
        if (uploadedFile) {
          console.log('File uploaded successfully:', uploadedFile);
          handleDocumentUpload(documentId, uploadedFile);
        }
      } else {
        // If it's already an uploaded file object, keep it as is
        console.log('File already uploaded, keeping as is:', file);
        handleDocumentUpload(documentId, file);
      }
    } catch (error) {
      console.error('Error handling file upload:', error);
      toast.error('Failed to upload files');
    }
  };

  const getRequiredDocumentVerifications = () => {
    if (!customPage || !customPage.documentVerifications || !documentVerifications.length) {
      return [];
    }

    return documentVerifications.filter(doc =>
      customPage.documentVerifications.some(requiredDoc =>
        requiredDoc === doc._id || requiredDoc._id === doc._id
      )
    );
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate submission name
    if (!submissionName || submissionName.trim() === '') {
      newErrors.name = 'Submission name is required';
    }

    customPage.fields.forEach(field => {
      if (field.required) {
        const value = formData[field.name];
        if (!value || (Array.isArray(value) && value.length === 0) ||
          (typeof value === 'string' && value.trim() === '')) {
          newErrors[field.name] = `${field.label} is required`;
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const updateData = {
        name: submissionName.trim(),
        data: formData
      };

      // Add Checklist if any are required
      const requiredDocs = getRequiredDocumentVerifications();
      if (requiredDocs.length > 0) {
        updateData.documentVerifications = requiredDocs.map(doc => {
          const uploadedFile = documentUploads[doc._id];
          
          // If file was uploaded, use the uploaded file URL
          // Otherwise, use the custom document's file URL as default
          const fileUrl = uploadedFile 
            ? uploadedFile.fileUrl 
            : (doc.file ? doc.file.url : null);
          
          const fileName = uploadedFile 
            ? uploadedFile.fileName 
            : (doc.file ? doc.file.name : doc.name);
          
          return {
            documentId: doc._id,
            documentName: doc.name,
            fileUrl: fileUrl,
            fileName: fileName,
            files:  []
          };
        });
      }

      console.log('EditSubmissionModal handleSubmit - updateData:', updateData);
      console.log('Document uploads state:', documentUploads);

      await onSave(updateData);
      onClose();
    } catch (error) {
      console.error('Error updating submission:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderField = (field) => {
    const value = formData[field.name] || '';
    const error = errors[field.name];

    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${error ? 'border-red-500' : 'border-gray-300'
              }`}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${error ? 'border-red-500' : 'border-gray-300'
              }`}
          />
        );

      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${error ? 'border-red-500' : 'border-gray-300'
              }`}
          />
        );

      case 'dropdown':
        return (
          <select
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${error ? 'border-red-500' : 'border-gray-300'
              }`}
          >
            <option value="">Select {field.label.toLowerCase()}</option>
            {field.options?.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case 'multitext':
        return (
          <textarea
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
            rows={4}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${error ? 'border-red-500' : 'border-gray-300'
              }`}
          />
        );

      case 'upload':
        return (
          <div className="text-sm text-gray-600">
            File uploads cannot be edited. Current files: {Array.isArray(value) ? value.length : 0} files
          </div>
        );

      case 'masterdata':
        const filteredMasterData = masterData.filter(item =>
          item.clientName?.toLowerCase().includes(masterDataSearchTerm.toLowerCase()) ||
          item.clientEmail?.toLowerCase().includes(masterDataSearchTerm.toLowerCase())
        );

        return (
          <div className="relative masterdata-dropdown">
            <input
              type="text"
              value={masterDataSearchTerm}
              onChange={(e) => {
                setMasterDataSearchTerm(e.target.value);
                setShowMasterDataDropdown(true);
              }}
              onFocus={() => setShowMasterDataDropdown(true)}
              placeholder={value ? value.clientName : "Search master data..."}
              className={`
                w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${error ? 'border-red-500' : 'border-gray-300'
                }
              `}
            />
            {showMasterDataDropdown && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                {filteredMasterData.map((item) => (
                  <div
                    key={item._id}
                    className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                    onClick={() => {
                      handleInputChange(field.name, item);
                      setMasterDataSearchTerm(item.clientName);
                      setShowMasterDataDropdown(false);
                    }}
                  >
                    <div className="font-medium">{item.clientName}</div>
                    <div className="text-sm text-gray-500">{item.clientEmail}</div>
                  </div>
                ))}
                {filteredMasterData.length === 0 && (
                  <div className="px-3 py-2 text-gray-500">No master data found</div>
                )}
              </div>
            )}
            {value && (
              <button
                type="button"
                onClick={() => {
                  handleInputChange(field.name, null);
                  setMasterDataSearchTerm('');
                }}
                className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>
        );

      case 'user':
        const filteredUsers = users.filter(user =>
          `${user.firstName} ${user.lastName}`.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
          user.email.toLowerCase().includes(userSearchTerm.toLowerCase())
        );

        return (
          <div className="relative user-dropdown">
            <input
              type="text"
              value={userSearchTerm}
              onChange={(e) => {
                setUserSearchTerm(e.target.value);
                setShowUserDropdown(true);
              }}
              onFocus={() => setShowUserDropdown(true)}
              placeholder={value ? `${value.firstName} ${value.lastName}` : "Search users..."}
              className={`
                w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${error ? 'border-red-500' : 'border-gray-300'
                }
              `}
            />
            {showUserDropdown && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                {filteredUsers.map((user) => (
                  <div
                    key={user._id}
                    className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                    onClick={() => {
                      handleInputChange(field.name, user);
                      setUserSearchTerm(`${user.firstName} ${user.lastName}`);
                      setShowUserDropdown(false);
                    }}
                  >
                    <div className="font-medium">{user.firstName} {user.lastName}</div>
                    <div className="text-sm text-gray-500">{user.email}</div>
                  </div>
                ))}
                {filteredUsers.length === 0 && (
                  <div className="px-3 py-2 text-gray-500">No users found</div>
                )}
              </div>
            )}
            {value && (
              <button
                type="button"
                onClick={() => {
                  handleInputChange(field.name, null);
                  setUserSearchTerm('');
                }}
                className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>
        );

      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${error ? 'border-red-500' : 'border-gray-300'
              }`}
          />
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Edit Submission</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Submission Name Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Submission Name *
            </label>
            <input
              type="text"
              value={submissionName}
              onChange={(e) => setSubmissionName(e.target.value)}
              placeholder="Enter a name for this submission..."
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
            />
            {errors.name && (
              <p className="text-sm text-red-600 mt-1">{errors.name}</p>
            )}
          </div>

          {/* Form Fields */}
          {customPage.fields.map((field, index) => (
            <div key={index}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>

              {renderField(field)}

              {errors[field.name] && (
                <p className="text-sm text-red-600 mt-1">{errors[field.name]}</p>
              )}
            </div>
          ))}

          {/* Checklist Section */}
          {getRequiredDocumentVerifications().length > 0 && (
            <div className="space-y-4 pt-4 border-t">
              <h3 className="text-lg font-medium text-gray-900">Required Checklist</h3>
              <p className="text-sm text-gray-600">
                The following documents are required for this submission. If no file is uploaded, the system will use the default file from the custom document:
              </p>

              <div className="space-y-4">
                {getRequiredDocumentVerifications().map((doc) => (
                  <div key={doc._id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="font-medium text-gray-900">{doc.name}</h4>
                        {doc.description && (
                          <p className="text-sm text-gray-600 mt-1">{doc.description}</p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Required
                        </span>
                        {(documentUploads[doc._id] && documentUploads[doc._id].length > 0) && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Custom Upload
                          </span>
                        )}
                        {(!documentUploads[doc._id] || documentUploads[doc._id].length === 0) && doc.file && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            Using Default
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Show existing files if any */}
                    {documentUploads[doc._id] ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                          <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                <span className="text-blue-600 text-xs font-medium">📄</span>
                              </div>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{documentUploads[doc._id].fileName}</p>
                              <p className="text-xs text-gray-500">
                                {documentUploads[doc._id].mimeType} • {documentUploads[doc._id].size ? `${(documentUploads[doc._id].size / 1024).toFixed(1)} KB` : 'Unknown size'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <a
                              href={documentUploads[doc._id].fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                            >
                              Download
                            </a>
                            <button
                              type="button"
                              onClick={() => {
                                handleDocumentUpload(doc._id, null);
                              }}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                        <div className="text-center pt-2">
                          <input
                            type="file"
                            onChange={(e) => {
                              console.log('Add More Files input change event:', e.target.files);
                              const files = Array.from(e.target.files);
                              console.log('Add More Files array:', files);
                              handleFileUpload(doc._id, files);
                            }}
                            multiple
                            className="hidden"
                            id={`file-upload-add-${doc._id}`}
                          />
                          <label
                            htmlFor={`file-upload-add-${doc._id}`}
                            className="cursor-pointer inline-flex items-center px-3 py-1 border border-gray-300 rounded-md shadow-sm text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
                          >
                            Replace File
                          </label>
                        </div>
                      </div>
                    ) : (
                      /* Show upload interface when no files exist */
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                        <div className="text-center">
                          <p className="text-sm text-gray-600 mb-2">
                            Drag and drop files here or click to browse
                          </p>
                          <input
                            type="file"
                            onChange={(e) => {
                              console.log('File input change event:', e.target.files);
                              const files = Array.from(e.target.files);
                              console.log('Files array:', files);
                              handleFileUpload(doc._id, files);
                            }}
                            multiple
                            className="hidden"
                            id={`file-upload-${doc._id}`}
                          />
                          <label
                            htmlFor={`file-upload-${doc._id}`}
                            className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                          >
                            Choose Files
                          </label>
                        </div>
                      </div>
                    )}

                    {doc.category && (
                      <p className="text-xs text-gray-500 mt-2">
                        Category: {doc.category}
                      </p>
                    )}

                    {doc.file && !submission?.documentVerifications?.some(dv => dv.documentId === doc._id) && (
                      <p className="text-xs text-blue-600 mt-2">
                        ✓ Default file available: {doc.file.originalName || doc.file.name}
                        {(!documentUploads[doc._id] || documentUploads[doc._id].length === 0) &&
                          " (will be used if no file is uploaded)"}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomPageSubmissionsTable;